import React from 'react';
import styled, { keyframes } from 'styled-components';
import { DATA_GRABS } from '../design';
import { COLORS, sp } from '../styles';
import Divider from './Divider';
import ThemedText from './ThemedText';

export interface DataRow {
    key: string;
    label?: string;
    value?: string;
    /** Renders the value as secondary paragraph text instead of primary. */
    isSecondaryValue?: boolean;
    isSeparator?: boolean;
    isLoading?: boolean;
    loadingValueWidth?: number | string;
    loadingLabelWidth?: number | string;
}

interface DataCardProps {
    rows: DataRow[];
    dataAuto?: string;
    children?: React.ReactNode;
    icon?: React.ReactNode;
    iconUrl?: string;
}

const Card = styled.div.attrs({ 'data-grab': DATA_GRABS.dividers })`
    display: flex;
    align-items: flex-start;
    padding: ${sp(2)};
    gap: ${sp(2)};
    border-radius: 8px;
    border: 1px solid ${COLORS.border};
`;

const IconSlot = styled.div`
    flex-shrink: 0;
    inline-size: 22px;
    block-size: 1lh;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${COLORS.icon};

    svg,
    img {
        inline-size: 22px;
        block-size: 22px;
        display: block;
    }
`;

const Data = styled.div`
    flex-grow: 1;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${sp(0.5)};
`;

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5em;
    flex-wrap: wrap;
`;

const Label = styled(ThemedText).attrs({
    textRole: 'primaryText' as const,
    defaultTag: 'span' as const,
})``;

const Value = styled(ThemedText).attrs({
    textRole: 'primaryText' as const,
    defaultTag: 'span' as const,
})``;

const SecondaryValue = styled(ThemedText).attrs({
    textRole: 'secondaryText' as const,
    defaultTag: 'span' as const,
})``;

const shimmer = keyframes`
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
`;

const Skeleton = styled.span<{ $width: number | string; $height: number }>`
    display: inline-block;
    inline-size: ${({ $width }) =>
        typeof $width === 'number' ? `${$width}px` : $width};
    block-size: ${({ $height }) => `${$height}px`};
    border-radius: 8px;
    background-color: ${COLORS.mutedFill};
    animation: ${shimmer} 1.2s ease-in-out infinite;
`;

const InnerDividerWrapper = styled.div`
    margin: 5px 0;
`;

const DataCard: React.FC<DataCardProps> = ({
    rows,
    dataAuto,
    children,
    icon,
    iconUrl,
}) => (
    <Card data-auto={dataAuto}>
        <IconSlot aria-hidden='true'>
            {iconUrl ? <img src={iconUrl} alt='' /> : icon}
        </IconSlot>
        <Data>
            {rows.map((row) => {
                if (row.isSeparator) {
                    return (
                        <InnerDividerWrapper key={row.key}>
                            <Divider />
                        </InnerDividerWrapper>
                    );
                }
                if (row.isLoading) {
                    return (
                        <Row
                            key={row.key}
                            data-auto={
                                dataAuto ? `${dataAuto}_${row.key}` : undefined
                            }
                        >
                            {row.label && <Label>{row.label}</Label>}
                            <Skeleton
                                $height={14}
                                $width={row.loadingLabelWidth ?? 82}
                            />
                            <Skeleton
                                $height={14}
                                $width={row.loadingValueWidth ?? 120}
                            />
                        </Row>
                    );
                }
                return (
                    <Row
                        key={row.key}
                        data-auto={
                            dataAuto ? `${dataAuto}_${row.key}` : undefined
                        }
                    >
                        {row.label && <Label>{row.label}</Label>}
                        {row.value &&
                            (row.isSecondaryValue ? (
                                <SecondaryValue>{row.value}</SecondaryValue>
                            ) : (
                                <Value>{row.value}</Value>
                            ))}
                    </Row>
                );
            })}
            {children}
        </Data>
    </Card>
);

export default DataCard;
